import {XMarkIcon} from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import DynamicEditForm from '../DynamicEditForm.jsx';
import LoadingSpinner from '../LoadingSpinner.jsx';
import ModalError from '../Modal/ModalError.jsx';
import ModalLoading from '../Modal/ModalLoading.jsx';
import useParseCSV from '/src/hooks/useParseCSV.js';
import useQueryEffect from '/src/hooks/useQueryEffect.js';
import { useImportPlotsMutation } from '/src/redux/importExportApi.js';
import { stringifyDate } from '/src/utils/dateInput.js';

const formConfig = {
    autoFocus: true,
    saveText: 'Import',
    hideDiscard: true,
    hideRequiredHint: true,
    fields: [
        {
            key: 'file',
            title: 'CSV File',
            type: 'file',
            accept: '.csv',
            required: true,
        },
    ],
};

const headerMap = {
    // open: (/\bopen\b/i),
    section: (/\bsection\b/i),
    lot: (/lot(\s?num|$)/i),
    lotPart: (/lot(\s?letter|\s?part|$)/i),
    firstName: (/\bfirst\W?name\b/i),
    lastName: (/\blast\W?name\b/i),
    suffix: (/\bsuffix\b/i),
    notes: (/^((?!size)\s?(notes|comments)).*$/i),
    sizeNotes: (/^.*(size\s?(notes|comments)).*$/i),
    sqf: (/\bsqf\b/i),
    price: (/price|\$|usd/i),
    date: (/date/i),
    month: (/\bmonth\b/i),
    day: (/\bday\b/i),
    year: (/\byear\b/i),
}

const displayHeaders = [
    'section',
    'lot',
    'lotPart', 
    'notes',
    'sizeNotes', 
    'sqf',
    'price',
    'date',
    'plotOwners',
]

export default function MapDataImport({onClose}){
    const [error, setError] = useState(null);
    const [inputFile, setInputFile] = useState(null);
    const [parsedResult, setParsedResult] = useState(null);
    const [upload, { error: uploadError, isLoading: isUploading, isSuccess: isUploadSuccess, data: uploadResult }] = useImportPlotsMutation();
    const [success, setSuccess] = useState(false);

    const userTookAction = useQueryEffect(error=>{
        if(error){
            setError(error);
        }else{
            setSuccess(true);
        }
    },[isUploadSuccess],[uploadError]);

    // useEffect(()=>{
    //     if(inputFile){
    //         console.debug('inputFile', inputFile);
    //     }
    // },[inputFile]);

    const inputData = useParseCSV(inputFile);

    useEffect(()=>{
        if(inputData){
            try {
                // console.debug('inputData', inputData);
                const outItems = parseData(inputData);
                // console.debug('outputData:', outItems);
                setParsedResult(outItems);
                setInputFile(null);    
            } catch (error) {
                console.error('error parsing import data arr', error);
                setError(error);
                setInputFile(null);
            }
        }
    },[inputData]);

    function parseData(dataArr){
        // remove and store headers
        const headers = dataArr.shift();
        // remove data type row if needed
        if((/\(.+\)/).test(dataArr[0][0])){
            dataArr.shift();
        }
        // determine column mapping (header <--> field)
        const colMapping = [];
        const skippedCols = [];
        for(let key of Object.keys(headerMap)){
            if(headerMap.hasOwnProperty(key)){
                const index = headers.findIndex(header=>headerMap[key].test(header));
                if(index != -1){
                    colMapping[index] = key;
                }else{
                    skippedCols.push(key);
                }
            }
        }
        // console.debug('column mapping:', colMapping);
        if(skippedCols.length){
            const splitDate = ['month','day','year'];
            if(skippedCols.every(key=>splitDate.includes(key)) && !skippedCols.includes('date')){
                // if we skipped just the split date, but we have regular date, then ignore
            }else if(skippedCols.every(key=>key === 'date') && splitDate.every(key=>colMapping.includes(key))){
                // if we skipped just the regular date but have split date, then ignore
            }else{
                // we missed some other important cols
                console.warn("couldn't find matching header for the following keys", skippedCols);
                throw new Error(`Couldn't find a matching column header for the following keys, (are you sure this sheet is in the right format?) ${skippedCols.join(', ')}`)    
            }
        }
        // map data from each row to new object
        const newItems = {};
        let blankRows = 0;
        for(let row in dataArr){
            let newItem = {};
            let blank = true;
            // parse columns into key-value pairs
            for(let col in dataArr[row]){
                const key = colMapping[col];
                if(key){
                    const value = dataArr[row][col];
                    newItem[key] = (value === '')? undefined : value;
                    if(newItem[key] && blank) blank = false;
                }
            }
            if(blank){
                blankRows++;
                continue;
            }
            // normalize row-style lots
            // "Row 1 Lot 2B" --> K 12 B
            if((newItem.lotPart + '').toLowerCase().includes('row')){
                try {
                    const match = (/^\W?\brow\s?(\d+)\W+\bLot\s?(\d+)(\w?)\b.*$/i).exec(newItem.lotPart);
                    if(!match[1]) throw new Error("Can't determine the lot number of one or more row-style lots");
                    newItem.lot = '' + (match[1] || '') + (match[2] || '');
                    newItem.lotPart = match[3];
                } catch (error) {
                    console.warn('failed to convert row-style lot', newItem);
                }
            }
            // normalize complex lotParts
            // "A,B,C,D" | "A part B" --> AB
            if(newItem.lotPart && newItem.lotPart.length > 2){
                newItem.lotPart = newItem.lotPart.replace(/\Wpart\W|,/gi, '');
            }

            // check for existing
            const lotId = getLotId(newItem);
            if(!newItems[lotId]) newItems[lotId] = {};
            
            // separate lot owner
            newItem.open = !(newItem.firstName || newItem.lastName || newItem.suffix);
            if(!newItem.open){
                if(!newItems[lotId].plotOwners) newItems[lotId].plotOwners = [];
                newItems[lotId].plotOwners.push({
                    firstName: newItem.firstName,
                    lastName: newItem.lastName,
                    suffix: newItem.suffix,
                });
                delete newItem.firstName;
                delete newItem.lastName;
                delete newItem.suffix;
            }

            // normalize date 
            if(newItem.year && newItem.month && newItem.day){
                newItem.date = stringifyDate(newItem);
            }
            // normalize price 
            if(newItem.price){
                newItem.price = parseFloat((newItem.price + '').replace('$',''))
            }

            // save data to existing
            Object.assign(newItems[lotId], newItem);
        }
        
        // convert key-value by lot to array
        const outItems = [];
        for(let key of Object.keys(newItems)){
            if(newItems.hasOwnProperty(key)){
                outItems.push(newItems[key]);
            }
        }

        // console.debug(`found ${blankRows} blank rows`);

        if(!outItems.length){
            throw new Error('Found zero valid items');
        }

        return outItems;
    }

    function getLotId(item){
        return [item.section, item.lot, item.lotPart].join('-');
    }

    function reset(){
        setInputFile(null);
        setParsedResult(null);
        setError(null);
        setSuccess(false);
    }

    function onConfirmImport(data){
        // console.debug('uploading...', data);
        userTookAction();
        upload(data);
    }

    function onDone(){
        reset();
        if(onClose) onClose();
    }

    return (
        <div>
            {(isUploading || (inputFile && (!inputData || !parsedResult))) && <ModalLoading absolute={true} title="Working..." />}
            {error && <ModalError title="Error" message={error.message} description={error.status || error.code} callback={()=>setError(null)} />}
            <h4 className='text-xl m-4 font-light'>Data Import</h4>
            {(success && (
                <div className='py-3 px-4'>
                        {(!uploadResult && (
                            <p className='text-xl mb-4'>
                                <LoadingSpinner className='w-6' />
                            </p>
                        )) || ((uploadResult.insertCount > 0 || uploadResult.updateCount > 0) && (
                            <div className='mb-4'>
                                <p className='text-xl mb-2'>Successfully Imported {uploadResult.insertCount} New Records</p>  
                                {uploadResult.updateCount > 0 && <p>Updated {uploadResult.updateCount} Existing Records</p>}
                                {uploadResult.skipCount > 0 && <p>Skipped {uploadResult.skipCount} Duplicate Records</p>}
                            </div>
                        )) || ((
                            <div className='mb-4'>
                                <p className='text-xl mb-2'>No Records Imported</p>    
                                <p>All {uploadResult.skipCount} records already exist.</p>
                            </div>
                        ))}
                    <button className='btn' onClick={()=>onDone()}>Done</button>
                </div>
            )) || (parsedResult && (
                <div className='py-3 px-4 flex flex-col'>
                    <p>Does this information look correct?</p>
                    {/* <div className='mb-1 overflow-auto max-w-md max-h-[65vh] border border-black flex-grow'> */}
                    <div className='mb-1 overflow-auto max-w-md max-h-[50vh] border border-black flex-auto'>
                        <table>
                            <thead className='font-bold sticky top-0 bg-white uppercase shadow-md'>
                                <tr>
                                    {displayHeaders.map(key=>(
                                        <td key={key} className='p-2'>{key}</td>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {parsedResult.map(item=>(
                                    <tr key={getLotId(item)} className='odd:bg-gray-100'>
                                        {displayHeaders.map(key=>(
                                            <td key={key} title={item[key]? (item[key] + '') : undefined} className='border-r'>
                                                <span className='whitespace-nowrap max-w-[10rem] inline-block overflow-hidden text-ellipsis px-2 py-1'>{(item[key])? (Array.isArray(item[key])? item[key].length : (item[key] + '')) : "N/A"}</span>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className='font-light mb-4'>{parsedResult.length} Items</p>
                    <div className='flex group'>
                        <button onClick={()=>onConfirmImport(parsedResult)} className="btn group-hover:btn-invert">Yes, Proceed with Import</button>
                        <button onClick={()=>reset()} className="btn-invert">Cancel</button>
                    </div>
                </div>
            )) || (
                <div className='py-3 px-4'>
                    <DynamicEditForm formConfig={formConfig} selectedAsset={{file: inputFile}} onSave={data=>setInputFile(data.file)} />
                </div>
            )}
        </div>
    );
}